import { Box, VStack, Button, Text, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, Image, HStack, Collapse, Icon } from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { renderMenuIcon } from '@/lib/icons/menuIcons'
import { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

interface NavigationItemDef {
  name: string
  href: string
  icon: string
}

interface SidebarProps {
  items?: NavigationItemDef[]
  isOpen?: boolean
  onClose?: () => void
}

// Separate memoized logo component to prevent re-renders
const SidebarLogo = memo(({ logoUrl, appName }: { logoUrl: string; appName: string }) => (
  <Box 
    p={4} 
    pb={2}
    flexShrink={0}
    bg="var(--sidebar-bg)"
    position="sticky"
    top={0}
    zIndex={1}
  >
    {/* <HStack spacing={2} align="center"> */}
      <Image 
        key={logoUrl} 
        src={logoUrl} 
        alt="Deltrans Logo" 
        height="80px" 
        width="100%"
        objectFit="contain"
        loading="eager"
        onError={(e) => {
          // Fallback if image fails to load
          const target = e.target as HTMLImageElement
          if (target.src !== '/assets/img/deltrans-logo.png') {
            target.src = '/assets/img/deltrans-logo.png'
          }
        }}
      />
      {/* {appName ? <Text>| {appName}</Text> : null} */}
    {/* </HStack> */}
  </Box>
))

SidebarLogo.displayName = 'SidebarLogo'

function SidebarComponent({ items, isOpen = false, onClose }: SidebarProps) {
  const location = useLocation()
  const pathname = location.pathname || '/'
  const navigate = useNavigate()
  
  // Only subscribe to user_routes to prevent unnecessary re-renders when other user properties change
  const userRoutes = useAuthStore((state) => state.user?.user_routes)
  console.log('userRoutes', userRoutes);
  const borderColor = useThemeColors().border
  const { settings: siteSettings } = useSiteSettings()
  
  // Initialize openGroups from localStorage to persist across re-renders
  // Use lazy initializer to only read from localStorage once on mount
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-open-groups')
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, boolean>
        return parsed
      }
    } catch (e) {
      // Ignore parse errors
    }
    return {}
  })
  
  // Use refs to track manually opened/closed groups that should persist across navigation
  const manuallyOpenedRef = useRef<Set<string>>(new Set())
  const manuallyClosedRef = useRef<Set<string>>(new Set())
  const isInitializedRef = useRef(false)
  
  // Initialize refs from localStorage on mount (only once)
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      Object.keys(openGroups).forEach((key) => {
        if (openGroups[key]) {
          manuallyOpenedRef.current.add(key)
        }
      })
    }
  }, [openGroups])
  
  // Memoize database-driven values to prevent recalculation on every render
  // Extract values directly to prevent object reference changes
  const sidenavLogo = siteSettings?.sidenav_logo
  const siteName = siteSettings?.site_name
  
  // Add cache busting query parameter to logo URL (only when URL changes)
  const logoUrl = useMemo(() => {
    const baseUrl = sidenavLogo || '/assets/img/deltrans-logo.png'
    // Add version for cache busting (static version that changes only when logo changes)
    // In development, use a hash of the URL; in production, use build time
    const version = import.meta.env.DEV 
      ? `?v=${baseUrl.split('/').pop()?.replace(/[^a-zA-Z0-9]/g, '') || 'dev'}` 
      : `?v=${import.meta.env.VITE_BUILD_TIME || '1.0.0'}`
    return baseUrl.includes('?') ? baseUrl : `${baseUrl}${version}`
  }, [sidenavLogo])
  const appName = useMemo(() => 
    siteName || import.meta.env.VITE_APP_NAME || 'ADMIN PANEL',
    [siteName]
  )
  
  const sidebarBg = 'var(--sidebar-bg)'
  const sidebarText = 'var(--sidebar-text)'
  const sidebarHoverBg = 'var(--sidebar-hover-bg)'
  const sidebarActiveBg = 'var(--sidebar-active-bg)'
  
  // Memoize helper functions to prevent recreation on every render
  const joinPaths = useCallback((parent: string, child: string) => {
    const a = (parent || '').replace(/\/+$/, '')
    const b = (child || '').replace(/^\/+/, '')
    const joined = `${a}/${b}`
    return joined.startsWith('/') ? joined : `/${joined}`
  }, [])
  
  // Memoize routes to prevent unnecessary recalculations and re-renders
  const topLevelRoutes = useMemo(() => {
    const routes = Array.isArray(userRoutes) ? userRoutes : []
    
    // Debug logging to help diagnose navigation issues
    if (routes.length === 0 && userRoutes) {
      console.warn('[Sidebar] No routes found. userRoutes:', userRoutes)
    } else if (routes.length > 0) {
      console.log('[Sidebar] Total routes:', routes.length, 'Routes:', routes)
    }
    
    const filtered = routes.filter((r: any) => {
      // Only include routes with side_nav === 'true'
      if (r.side_nav !== 'true') {
        return false
      }
      
      // If route has children, check if at least one child should be displayed
      if (Array.isArray(r.children) && r.children.length > 0) {
        const displayableChildren = r.children.filter((c: any) => c.side_nav === 'true')
        // Show parent if it has at least one displayable child
        if (displayableChildren.length > 0) {
          return true
        }
        // If no displayable children but parent has side_nav === 'true',
        // it's a standalone route with functional children (create/edit routes with side_nav: false).
        // Still show the parent as a navigation item (e.g., Shipping Lines).
        // The children will be filtered out during rendering since they have side_nav === 'false'
        return true
      }
      
      // If route has no children, include it (it's a standalone route)
      return true
    })
    
    if (filtered.length === 0 && routes.length > 0) {
      console.warn('[Sidebar] No routes with side_nav=true. All routes:', routes.map((r: any) => ({
        name: r.name,
        path: r.path,
        side_nav: r.side_nav
      })))
    }
    
    return filtered
  }, [userRoutes])

  const isActivePath = useCallback((targetPath: string) => {
    const norm = (p: string) => ('/' + (p || '').replace(/^\/+/, '')).replace(/\/+$/, '')
    const cur = norm(pathname)
    const tgt = norm(targetPath)
    return cur === tgt || cur.startsWith(tgt + '/')
  }, [pathname])

  useEffect(() => {
    // Auto-open any parent whose child is active
    // This should only ADD groups that need to be open, never remove manually opened ones
    // But respect manually closed groups (don't auto-open them even if they have active children)
    const groupsToOpen: Record<string, boolean> = {}
    topLevelRoutes.forEach((route: any) => {
      const groupKey = route.path || route.name
      // Skip if this group was manually closed
      if (manuallyClosedRef.current.has(groupKey)) {
        return
      }
      if (Array.isArray(route.children)) {
        const shouldOpen = route.children.some((c: any) => isActivePath(joinPaths(route.path, c.path)))
        if (shouldOpen || isActivePath(route.path)) {
          groupsToOpen[groupKey] = true
        }
      } else if (isActivePath(route.path)) {
        groupsToOpen[groupKey] = true
      }
    })
    
    // Merge with previous state to preserve manually opened groups
    // Only add groups that should be open, never remove manually opened ones
    setOpenGroups((prev) => {
      const next = { ...prev }
      let hasChanges = false
      
      // Add groups that should be open for active paths (unless manually closed)
      Object.keys(groupsToOpen).forEach((key) => {
        if (!next[key] && !manuallyClosedRef.current.has(key)) {
          next[key] = true
          hasChanges = true
        }
      })
      
      // Preserve all manually opened groups (tracked in ref)
      manuallyOpenedRef.current.forEach((key) => {
        if (!next[key]) {
          next[key] = true
          hasChanges = true
        }
      })
      
      // Only update if there are actual changes
      if (!hasChanges) return prev
      return next
    })
  }, [pathname, topLevelRoutes, isActivePath, joinPaths])

  // Helper to check if parent has active child
  const hasActiveChild = useCallback((route: any) => {
    if (!Array.isArray(route.children)) return false
    return route.children.some((c: any) => {
      if (c.side_nav !== 'true') return false
      const childPath = joinPaths(route.path, c.path)
      return isActivePath(childPath)
    })
  }, [isActivePath, joinPaths])

  // Memoize renderNavButtons to prevent recreation
  const renderNavButtons = useCallback((routes: any[]) => (
    routes.map((route: any) => {
      const hasChildren = Array.isArray(route.children) && route.children.length > 0
      if (hasChildren) {
        const childNavs = route.children.filter((c: any) => c.side_nav === 'true')
        // If route has children but all are hidden (side_nav === 'false'),
        // treat it as a standalone route (e.g., Shipping Lines with create/edit children)
        if (childNavs.length === 0) {
          const isRouteActive = isActivePath(route.path)
          return (
            <Button
              key={route.path}
              variant="ghost"
              justifyContent="flex-start"
              leftIcon={renderMenuIcon(route.icon)}
              onClick={(e) => {
                e.preventDefault()
                navigate(route.path)
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isRouteActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isRouteActive ? 'semibold' : 'normal'}
            >
              {route.name}
            </Button>
          )
        }
        const groupKey = route.path || route.name
        const isOpen = !!openGroups[groupKey]
        // Parent is active if the parent path is active OR if it has an active child
        const isParentActive = isActivePath(route.path) || hasActiveChild(route)
        
        return (
          <Box key={route.path}>
            <Button
              variant="ghost"
              justifyContent="flex-start"
              leftIcon={renderMenuIcon(route.icon)}
              rightIcon={<Icon as={isOpen ? ChevronDown : ChevronRight} />}
              onClick={() => {
                setOpenGroups(prev => {
                  const newState = !prev[groupKey]
                  // Track manually opened/closed groups
                  if (newState) {
                    manuallyOpenedRef.current.add(groupKey)
                    manuallyClosedRef.current.delete(groupKey)
                  } else {
                    manuallyOpenedRef.current.delete(groupKey)
                    manuallyClosedRef.current.add(groupKey)
                  }
                  const updated = { ...prev, [groupKey]: newState }
                  // Persist to localStorage when manually changed
                  try {
                    localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                  } catch (e) {
                    // Ignore storage errors
                  }
                  return updated
                })
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isParentActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isParentActive ? 'semibold' : 'normal'}
              w="100%"
              pr={2}
            >
              {route.name}
            </Button>
            <Collapse in={isOpen} animateOpacity>
              <VStack spacing={1} pl={6} align="stretch" mt={1}>
                {childNavs.map((child: any) => {
                  const childPath = joinPaths(route.path, child.path)
                  const isChildActive = isActivePath(childPath)
                  return (
                    <Button
                      key={child.path}
                      variant="ghost"
                      justifyContent="flex-start"
                      leftIcon={renderMenuIcon(child.icon)}
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(childPath)
                      }}
                      color={sidebarText}
                      _hover={{ bg: sidebarHoverBg }}
                      bg={isChildActive ? sidebarActiveBg : 'transparent'}
                      fontWeight={isChildActive ? 'semibold' : 'normal'}
                    >
                      {child.name}
                    </Button>
                  )
                })}
              </VStack>
            </Collapse>
          </Box>
        )
      }
      const isRouteActive = isActivePath(route.path)
      return (
        <Button
          key={route.path}
          variant="ghost"
          justifyContent="flex-start"
          leftIcon={renderMenuIcon(route.icon)}
          onClick={(e) => {
            e.preventDefault()
            navigate(route.path)
          }}
          color={sidebarText}
          _hover={{ bg: sidebarHoverBg }}
          bg={isRouteActive ? sidebarActiveBg : 'transparent'}
          fontWeight={isRouteActive ? 'semibold' : 'normal'}
        >
          {route.name}
        </Button>
      )
    })
  ), [openGroups, isActivePath, hasActiveChild, joinPaths, navigate, sidebarText, sidebarHoverBg, sidebarActiveBg])

  const fallbackItems = useMemo(() => (items || []).map(it => (
    <Button
      key={it.href}
      variant="ghost"
      justifyContent="flex-start"
      leftIcon={renderMenuIcon(it.icon)}
      onClick={(e) => {
        e.preventDefault()
        navigate(it.href)
      }}
      color={sidebarText}
      _hover={{ bg: sidebarHoverBg }}
    >
      {it.name}
    </Button>
  )), [items, navigate, sidebarText, sidebarHoverBg])

  return (
    <>
    {/* Desktop sidebar */}
    <Box
      w={{ base: 0, md: 64 }}
      bg={sidebarBg}
      borderRight="1px"
      borderColor={borderColor}
      h="100vh"
      maxH="100vh"
      display={{ base: 'none', md: 'flex' }}
      flexDirection="column"
      color={sidebarText}
      overflow="hidden"
    >
      <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
        {/* Fixed Logo Section - Memoized to prevent re-renders */}
        {/* <SidebarLogo logoUrl={logoUrl} appName={appName} /> */}
        <SidebarLogo logoUrl={logoUrl} appName={''} />
        
        {/* Scrollable Navigation Section */}
        <VStack 
          spacing={2} 
          align="stretch" 
          p={4}
          pt={2}
          pr={2}
          flex="1" 
          overflowY="auto" 
          overflowX="hidden"
          css={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
        </VStack>
      </Box>
    </Box>

    {/* Mobile drawer */}
    <Drawer isOpen={!!isOpen} placement="left" onClose={onClose || (() => {})}>
      <DrawerOverlay />
      <DrawerContent bg={sidebarBg} color={sidebarText}>
        <DrawerCloseButton />
        <DrawerHeader>
          {/* <HStack spacing={2} align="center"> */}
            <Image src={logoUrl} alt="Deltrans Logo" height="80px" width="100%" objectFit="contain" />
            {/* {appName ? <Text>| {appName}</Text> : null} */}
          {/* </HStack> */}
        </DrawerHeader>
        <DrawerBody>
          <VStack spacing={4} align="stretch">
            {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
    </>
  )
}

// Memoize the component to prevent re-renders when props haven't changed
export const Sidebar = memo(SidebarComponent, (prevProps, nextProps) => {
  // Only re-render if isOpen, onClose, or items actually change
  // Use deep comparison for items array to prevent unnecessary re-renders
  let itemsEqual = prevProps.items === nextProps.items
  if (!itemsEqual && prevProps.items && nextProps.items) {
    const prevItems = prevProps.items
    const nextItems = nextProps.items
    itemsEqual = prevItems.length === nextItems.length &&
      prevItems.every((item, idx) => {
        const nextItem = nextItems[idx]
        return nextItem && item.href === nextItem.href &&
          item.name === nextItem.name &&
          item.icon === nextItem.icon
      })
  }
  
  return prevProps.isOpen === nextProps.isOpen && 
         prevProps.onClose === nextProps.onClose &&
         itemsEqual
})

export default Sidebar

